import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { ConnectedFocusError } from "focus-formik-error";
import {
  getTicketFees,
  getTicketingFeeSummary,
  numberWithCommas,
} from "../../../../../components/utilities/UtilityFunctions";
import ModalLoadingFull from "../../../../../components/modals/ModalLoadingFull";
import HelpButton from "../../../../../components/utilities/HelpButton";
import {
  BCPAngleDown,
  BCPCircleCheck,
} from "../../../../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../../../../global/environmentVariables";
import { useNavigate } from "react-router";
const IS_UY = getEnvironmentVariable("IS_UY") === "true";

export default function EventCreationRegistraion({
  getInitialValues,
  getValidationSchema,
  submitEvent,
  setEventValues,
  eventValues,
  hasSubAccess,
  theme,
  readOnly,
  hasTicketingAccess,
  eventId,
  event,
  step,
  setStep,
  EventStepper,
  circuits,
  gameStore,
  authContext,
}) {
  const [initialValues, setInitialValues] = useState(null);
  const [ticketingFees, setTicketingfees] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feeProcessing, setFeeProcessing] = useState(false);

  const ticketFeeSummary = (values) => {
    return getTicketingFeeSummary(
      values?.numTickets || 0,
      values?.ticketPrice || 0,
      values?.playerPaysFees && ticketingFees?.FixedPlayer !== undefined
        ? ticketingFees?.FixedPlayer
        : ticketingFees?.Fixed,
      values?.playerPaysFees && ticketingFees?.PercentPlayer !== undefined
        ? ticketingFees?.PercentPlayer
        : ticketingFees?.Percent,
      !(values?.playerPaysFees || false)
    );
  };

  const getTicketingFees = async (_currency) => {
    setFeeProcessing(true);
    const queryParams = {
      currency: _currency?.toUpperCase(),
    };

    if (gameStore?.id || event?.gameStore?.id) {
      queryParams.gameStoreId = gameStore?.id || event?.gameStore?.id;
    } else {
      queryParams.ownerId =
        event?.ownerId ||
        event?.owner?.id ||
        authContext?.authState?.identity?.userId;
    }

    if (event?.id || eventId) queryParams.eventId = event?.id || eventId;

    const _ticketingFees = await getTicketFees(queryParams);
    setTicketingfees(_ticketingFees);
    setFeeProcessing(false);
  };
  const navigate = useNavigate();
  const form = document.querySelector("form");
  useEffect(() => {
    if (initialValues) {
      let _currency = (initialValues?.ticketCurrency || "USD")?.toUpperCase();
      let _ticketingFees = initialValues?.ticketingFees;
      if (!_ticketingFees) {
        getTicketingFees(_currency);
      } else {
        setTicketingfees(_ticketingFees);
      }
    } else {
      let _initialValues = getInitialValues();
      setInitialValues(_initialValues);
    }
    setLoading(false);

    // eslint-disable-next-line
  }, [initialValues]);

  return (
    <Grid item xs={12} sx={{ pt: 2 }}>
      {loading ? (
        <ModalLoadingFull />
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={getValidationSchema()}
          onSubmit={submitEvent}
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form onSubmit={handleSubmit}>
              {/* Event Creation Progress Stepper */}
              <EventStepper
                step={step}
                setStep={setStep}
                event={event}
                circuits={circuits}
              />

              <ConnectedFocusError />

              <Grid
                container
                spacing={2}
                pt={1}
                onBlur={() => {
                  setEventValues(
                    eventValues ? { ...eventValues, ...values } : { ...values }
                  );
                }}
              >
                <Grid item xs={12}>
                  <Divider
                    sx={{
                      mt: 1,
                      mb: 1,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h4" fontWeight="700">
                    Player Registration
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <RadioGroup
                    defaultValue={!values.usingOnlineReg}
                    disabled={readOnly}
                  >
                    <FormControlLabel
                      checked={!values.usingOnlineReg}
                      control={<Radio />}
                      onChange={() => {
                        setFieldValue("usingOnlineReg", false);
                      }}
                      label={
                        <Typography className="MuiFormControlLabel-label">
                          Manually enter roster & registration
                          <HelpButton
                            title={`After you create your event, you can manually add players to your Roster. This is most helpful for smaller events that are not open to the public.`}
                          />
                        </Typography>
                      }
                      disabled={readOnly}
                    />
                    <FormControlLabel
                      checked={values.usingOnlineReg}
                      control={<Radio />}
                      onChange={() => {
                        setFieldValue("usingOnlineReg", true);
                        getTicketingFees(values?.ticketCurrency || "USD");
                      }}
                      label={
                        <Typography className="MuiFormControlLabel-label">
                          Use BCP's online registration
                          <HelpButton
                            title={`Allow players to register online for your event through BCP. This is most helpful for public events and for facilitating ticket sales (valid bank account must be linked).`}
                          />
                        </Typography>
                      }
                      disabled={readOnly}
                    />
                  </RadioGroup>
                </Grid>

                <Grid item xs={12}>
                  <Divider
                    sx={{
                      mt: 1,
                      mb: 1,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    label={
                      <Box
                        sx={{
                          lineHeight: "16px",
                          color: hasSubAccess
                            ? "text.primary"
                            : theme?.palette?.text?.disabled,
                        }}
                      >
                        <Typography className="MuiFormControlLabel-label">
                          {`Private Event (${getEnvironmentVariable(
                            "APP_NAME"
                          )} Subscription required)`}
                        </Typography>
                        <Typography
                          className="MuiFormControlLabel-label"
                          variant="caption"
                        >
                          {`Private events require players to enter a unique access code to register for your event.`}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      ml: 0,
                      width: "100%",
                      "& .MuiFormControlLabel-label": {
                        width: "calc(100% - 58px)",
                      },
                    }}
                    control={
                      <Switch
                        checked={values.privateEvent}
                        onChange={() => {
                          setFieldValue("privateEvent", !values?.privateEvent);
                        }}
                        disabled={hasSubAccess ? false : true || readOnly}
                      />
                    }
                    disabled={hasSubAccess ? false : true || readOnly}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider
                    sx={{
                      mt: 1,
                      mb: 1,
                    }}
                  />
                </Grid>

                {values?.usingOnlineReg && (
                  <>
                    {values?.eventType !== "Teams Event" &&
                      values?.eventType !== "Doubles Event" && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="h4" fontWeight="700">
                              Shipping Details
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Typography variant="boxy1">{`Do you need to collect players' physical addresses for prizing, eligibility, and/or shipment of physical goods? If so, this will be a required field for players when they register.`}</Typography>

                            <RadioGroup
                              defaultValue={values?.shippingDetails?.requested}
                              sx={{ mt: 2 }}
                            >
                              <FormControlLabel
                                value={!values?.shippingDetails?.requested}
                                checked={!values?.shippingDetails?.requested}
                                control={<Radio />}
                                onClick={() => {
                                  setFieldValue(
                                    "shippingDetails.requested",
                                    false
                                  );
                                  setFieldValue(
                                    "shippingDetails.mandatory",
                                    false
                                  );
                                }}
                                label={
                                  <Box sx={{ lineHeight: "16px" }}>
                                    <Typography
                                      className="MuiFormControlLabel-label"
                                      variant="body1"
                                    >
                                      No
                                    </Typography>
                                  </Box>
                                }
                                disabled={readOnly}
                              />
                              <FormControlLabel
                                value={values?.shippingDetails?.requested}
                                checked={values?.shippingDetails?.requested}
                                control={<Radio />}
                                onClick={() => {
                                  setFieldValue(
                                    "shippingDetails.requested",
                                    true
                                  );
                                  setFieldValue(
                                    "shippingDetails.mandatory",
                                    true
                                  );
                                }}
                                label={
                                  <Typography className="MuiFormControlLabel-label">
                                    Yes
                                  </Typography>
                                }
                                disabled={readOnly}
                              />
                            </RadioGroup>

                            {values?.shippingDetails?.requested && (
                              <>
                                <Typography
                                  className="MuiFormControlLabel-label"
                                  sx={{ mt: 2 }}
                                >
                                  Shipping Description (Optional)
                                  <HelpButton title="It is recommended to explain to your players what you are using their shipping details for, eg. the shipping of prizes for an online event." />
                                </Typography>
                                <TextField
                                  name="shippingDetails.description"
                                  value={values.shippingDetails.description}
                                  variant="outlined"
                                  fullWidth
                                  type="text"
                                  placeholder="Shipping Description"
                                  onChange={handleChange}
                                  disabled={readOnly}
                                  multiline
                                  rows={2}
                                />
                              </>
                            )}
                          </Grid>

                          <Grid item xs={12}>
                            <Divider
                              sx={{
                                mt: 1,
                                mb: 1,
                              }}
                            />
                          </Grid>
                        </>
                      )}

                    <Grid item xs={12}>
                      <Typography variant="h4" fontWeight="700">
                        Ticket Details
                      </Typography>
                    </Grid>

                    {/* TODO: Add ticketing options here */}
                    <Grid item xs={12}>
                      <TextField
                        name="numTickets"
                        label="Number of Tickets"
                        value={values.numTickets}
                        variant="outlined"
                        fullWidth
                        required
                        type="number"
                        onBlur={handleChange}
                        onChange={handleChange}
                        error={Boolean(
                          touched.numTickets && values.numTickets === ""
                        )}
                        helperText={
                          touched.numTickets && values.numTickets === ""
                            ? "Required"
                            : null
                        }
                        disabled={readOnly}
                      />
                    </Grid>

                    {!IS_UY && hasTicketingAccess && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            name="ticketPrice"
                            label="Price"
                            value={values.ticketPrice}
                            variant="outlined"
                            fullWidth
                            required
                            type="number"
                            onBlur={(e) => {
                              setFieldValue("pricingDict", {
                                [values.currency || "usd"]: Math.round(
                                  e.target.value * 100
                                ),
                              });
                              handleChange(e);
                            }}
                            onChange={(e) => {
                              setFieldValue("pricingDict", {
                                [values.currency || "usd"]: Math.round(
                                  e.target.value * 100
                                ),
                              });
                              handleChange(e);
                            }}
                            error={Boolean(
                              touched.ticketPrice && values.ticketPrice === ""
                            )}
                            helperText={
                              touched.ticketPrice && values.ticketPrice === ""
                                ? "Required"
                                : null
                            }
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  {values.currency?.toUpperCase()}
                                </InputAdornment>
                              ),
                            }}
                            disabled={readOnly}
                          />
                          {values?.ticketPrice === 0 && (
                            <Typography
                              variant="caption"
                              sx={{ textAlign: "center" }}
                            >{`If the ticket cost is set to 0, this is a free event.`}</Typography>
                          )}
                        </Grid>

                        {values?.ticketPrice > 0 && (
                          <>
                            <Grid item xs={12}>
                              <Typography variant="h4">
                                Who will cover the{" "}
                                {feeProcessing ? (
                                  <CircularProgress size={12} />
                                ) : (
                                  `${numberWithCommas(
                                    (ticketFeeSummary(values).fee || 0).toFixed(
                                      2
                                    )
                                  )} ${values.currency?.toUpperCase()}`
                                )}{" "}
                                transaction fee?
                              </Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <ButtonGroup fullWidth>
                                <Button
                                  variant={
                                    values?.playerPaysFees
                                      ? "outlined"
                                      : "contained"
                                  }
                                  onClick={() => {
                                    setFieldValue("playerPaysFees", false);
                                    getTicketingFees(
                                      values?.ticketCurrency || "USD"
                                    );
                                  }}
                                  startIcon={
                                    !values?.playerPaysFees && (
                                      <BCPCircleCheck />
                                    )
                                  }
                                >
                                  Organizer
                                </Button>
                                <Button
                                  variant={
                                    values?.playerPaysFees
                                      ? "contained"
                                      : "outlined"
                                  }
                                  onClick={() => {
                                    setFieldValue("playerPaysFees", true);
                                    getTicketingFees(
                                      values?.ticketCurrency || "USD"
                                    );
                                  }}
                                  startIcon={
                                    values?.playerPaysFees && <BCPCircleCheck />
                                  }
                                >
                                  Player
                                </Button>
                              </ButtonGroup>
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="caption">
                                {`BCP charges a transaction fee per ticket. If the player is covering the fee, it will be shown as separate from the ticket price.`}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sx={{ minHeight: "90px" }}>
                              {feeProcessing ? (
                                <></>
                              ) : (
                                <>
                                  <Typography
                                    variant="body2"
                                    sx={{ textDecoration: "underline" }}
                                    gutterBottom
                                  >
                                    Per Ticket
                                  </Typography>
                                  <Typography variant="body1">
                                    Organizer will make:{" "}
                                    {numberWithCommas(
                                      (
                                        (ticketFeeSummary(values).amount || 0) -
                                        (ticketFeeSummary(values).fee || 0)
                                      ).toFixed(2)
                                    )}{" "}
                                    {values.currency?.toUpperCase()}
                                  </Typography>
                                  <Typography variant="body1">
                                    Player will pay (total):{" "}
                                    {numberWithCommas(
                                      (
                                        ticketFeeSummary(values).amount || 0
                                      ).toFixed(2)
                                    )}{" "}
                                    {values.currency?.toUpperCase()}
                                  </Typography>
                                </>
                              )}
                            </Grid>

                            {gameStore?.chargeType === "square" && (
                              <Grid item xs={12}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "warning.main" }}
                                >
                                  {`Tickets purchased through Square will not be able to be refunded through BCP. You must refund them through Square directly. Ticketing fees for Square tickets are in addition to any fees that Square may charge you. If you choose the pass fees to the player, they will ONLY be charged the BCP fees and not any Square fees.`}
                                </Typography>
                              </Grid>
                            )}

                            <Grid item xs={12}>
                              <TextField
                                name="refundDate"
                                label="Refund Date"
                                variant="outlined"
                                fullWidth
                                onBlur={handleChange}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.refundDate && values.refundDate === ""
                                )}
                                helperText={
                                  touched.refundDate && values.refundDate === ""
                                    ? "Required"
                                    : null
                                }
                                disabled={
                                  readOnly || gameStore?.chargeType === "square"
                                }
                                type="datetime-local"
                                defaultValue={values.refundDate}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                sx={{
                                  colorScheme:
                                    theme?.palette?.mode === "dark"
                                      ? "dark"
                                      : null,
                                }}
                              />
                            </Grid>
                          </>
                        )}
                      </>
                    )}

                    <Grid item xs={12}>
                      <Divider
                        sx={{
                          mt: 1,
                          mb: 1,
                        }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Accordion
                    sx={{
                      width: "100%",
                      backgroundColor:
                        theme?.palette?.mode === "dark"
                          ? "background.secondary"
                          : "background.primary",
                    }}
                  >
                    <AccordionSummary expandIcon={<BCPAngleDown />}>
                      <Typography variant="body1">Player Check-In</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <FormControl>
                        <RadioGroup
                          defaultValue={values?.disableCheckin}
                          name="radio-buttons-group"
                        >
                          <FormControlLabel
                            value={!values?.disableCheckin}
                            checked={!values?.disableCheckin}
                            control={<Radio />}
                            onChange={() => {
                              setFieldValue("disableCheckin", false);
                            }}
                            label={
                              <Box sx={{ lineHeight: "16px" }}>
                                <Typography
                                  className="MuiFormControlLabel-label"
                                  variant="body1"
                                >
                                  Players can check-in themselves with their
                                  phones.
                                </Typography>
                                <Typography variant="caption">
                                  {`Check-In opens 24 hours prior to the event start time. Players must have a free BCP account to do this`}
                                </Typography>
                              </Box>
                            }
                            disabled={readOnly}
                          />
                          <FormControlLabel
                            value={values?.disableCheckin}
                            checked={values?.disableCheckin}
                            control={<Radio />}
                            onChange={() => {
                              setFieldValue("disableCheckin", true);
                            }}
                            label={
                              <Typography className="MuiFormControlLabel-label">
                                Players must check-in with an organizer in
                                person.
                              </Typography>
                            }
                            disabled={readOnly}
                            sx={{ mt: 1 }}
                          />
                        </RadioGroup>
                      </FormControl>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      if (
                        !event?.id &&
                        (circuits?.length || event?.circuits?.length)
                      ) {
                        setStep(5);
                        navigate(
                          `${window.location.pathname}?active_tab=circuits`,
                          {
                            replace: false,
                          }
                        );
                        window.scrollTo(0, 0);
                      } else {
                        form.requestSubmit();
                      }
                    }}
                  >
                    {eventId
                      ? "Save Changes"
                      : circuits?.length > 0
                        ? "Next: Circuits"
                        : "Create Event"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      )}

      {feeProcessing && <ModalLoadingFull />}
    </Grid>
  );
}
